import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Collapse from '@mui/material/Collapse';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { WaypointType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { type JSX, useCallback, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { AutocompleteFormField } from '~components/FormFields/AutocompleteFormField';
import PhoneCodeFlagInput from '~components/FormFields/v2/PhoneCodeFlagInput';
import { PseudoLink } from '~components/Helpers/PseudoLink';
import { SiteSelection } from '~components/Order/SiteSelection/SiteSelection';
import { ContactItem } from '~hooks/useContact/models/ContactItem';
import type { Site, SiteBasic, WayPoint } from '~hooks/useSites/models';
import { useSites } from '~hooks/useSites/useSites';
import { createWaypoint } from '~pages/Sales/Orders/NewOrderForm/utils';
import type { Nullable } from '~types/Nullable';

import { type ProjectFormSchemaInterface } from '../../projectFormSchema';
import { useProjectFormV2Context } from '../ProjectFormV2Context';

// `WaypointFormValue` mirrors the runtime shape `setDefaultProjectValues`
// produces for `pickUpWayPoint` / `dropOffWayPoint` / `stagingWayPoint`.
// The legacy form derives the same type via
// `ReturnType<typeof setDefaultProjectValues>['pickUpWayPoint']`; v2 reads it
// off the schema interface directly so the form-side and runtime helpers stay
// drift-free.
type WaypointFormValue = ProjectFormSchemaInterface['pickUpWayPoint'];

// Form-shape factory parallel to the legacy form. The runtime
// `createWaypoint` from NewOrderForm/utils returns the `WayPoint` class shape
// used for local SiteSelection display state — that's distinct from the
// `WaypointFormValue` shape required by `setValue('pickUpWayPoint', ...)`.
function createWaypointFormValue(
  site: Site,
  type: WaypointType,
  ordinality: number,
  waypointID?: string,
  contact?: { name?: string; phone?: string },
): NonNullable<WaypointFormValue> {
  // `discardedAt` on `site.discardedAt` is the soft-delete marker; the legacy
  // form mistakenly lifted it onto the waypoint shape (where the schema
  // does NOT declare it — see waypointSchemaOptionalSites in
  // src/constants/regexConst.ts). v2 keeps the data alongside `site`
  // where the InferType actually accepts it.
  return {
    id: waypointID ?? '',
    type,
    ordinality,
    contact: {
      name: contact?.name ?? '',
      phone: contact?.phone ?? '',
    },
    site: {
      id: site.id,
      name: site.name,
      lat: site.lat ?? undefined,
      lon: site.lon ?? undefined,
      address: site.address ?? undefined,
      discardedAt: site.discardedAt?.toISOString() ?? undefined,
    },
  };
}

// Mirrors the legacy form (Location details card + the two
// contact toggles). Render-only by design: the cascading reset
// (company → account null) lives in ProjectFormV2Provider.
// Site-contact fetches fire from SiteSelection's onSiteChange callbacks
// instead of a panel-side effect — the panel grep gate forbids reactive
// hooks (effects / watchers / flag reads) inside `panels/`.
//
// The inline "Create new site" form from the legacy form is intentionally
// dropped: `showCreateSiteForm` is never set true in the legacy form (the
// state is dead code; the active create-site flow lives inside
// <SiteSelection/> itself via useSiteFormV2).
export function LocationDetailsPanel(): JSX.Element {
  const { control, formState, setValue, trigger } =
    useFormContext<ProjectFormSchemaInterface>();
  const { defaultProject, watchedSelectedCompany } = useProjectFormV2Context();
  const { getSiteContacts } = useSites();

  const defaultPickUpWaypoint = defaultProject?.pickUpWayPoints?.[0];
  const defaultDropOffWaypoint = defaultProject?.dropOffWayPoints?.[0];
  const defaultStagingWaypoint = defaultProject?.stagingWayPoints?.[0];

  const [pickUpWaypoint, setPickUpWaypoint] = useState<Nullable<WayPoint>>(
    defaultPickUpWaypoint ?? null,
  );
  const [dropOffWaypoint, setDropOffWaypoint] = useState<Nullable<WayPoint>>(
    defaultDropOffWaypoint ?? null,
  );
  const [stagingWaypoint, setStagingWaypoint] = useState<Nullable<WayPoint>>(
    defaultStagingWaypoint ?? null,
  );

  const [showPickUpContact, setShowPickUpContact] = useState<boolean>(
    Boolean(defaultPickUpWaypoint?.contact?.name),
  );
  const [showDropOffContact, setShowDropOffContact] = useState<boolean>(
    Boolean(defaultDropOffWaypoint?.contact?.name),
  );

  const [pickUpContacts, setPickUpContacts] = useState<ContactItem[]>([]);
  const [dropOffContacts, setDropOffContacts] = useState<ContactItem[]>([]);
  const [lastSelectedPickUpPhone, setLastSelectedPickUpPhone] = useState('');
  const [lastSelectedDropOffPhone, setLastSelectedDropOffPhone] = useState('');
  // The Name AutocompleteFormField needs the same force-remount key the
  // Phone field uses: MUI Autocomplete with `freeSolo` caches an internal
  // `inputValue` that does NOT re-sync from `value` when the form value is
  // updated externally (here, by `populateSiteContact`'s setValue). Without
  // a key change the field stays blank while Phone — which already had
  // `key=…${lastSelectedPickUpPhone}` — renders the fetched contact.
  const [lastSelectedPickUpName, setLastSelectedPickUpName] = useState('');
  const [lastSelectedDropOffName, setLastSelectedDropOffName] = useState('');

  // Per-waypoint nonce guarding the async site-contact fetch in
  // `populateSiteContact`. That fetch awaits `getSiteContacts` then writes the
  // contact; if the user re-picks (or clears) the same waypoint's site while a
  // fetch is in flight, an out-of-order resolution would stomp the active
  // site's contact with a stale one (pick A → pick B → A resolves last and
  // overwrites B). We record the latest requested site id per waypoint
  // synchronously on every pick/clear (in the onSiteChange handlers) and bail
  // after the await when the resolved fetch is no longer the active selection.
  // `getSiteContacts` takes no AbortSignal, so a ref-nonce is the self-
  // contained fix (and the panel grep gate forbids effects/watchers anyway).
  const latestRequestedSiteIdRef = useRef<{
    pickUpWayPoint: Nullable<string>;
    dropOffWayPoint: Nullable<string>;
  }>({ pickUpWayPoint: null, dropOffWayPoint: null });

  const companyId = watchedSelectedCompany?.id;
  const projectIdForDataFetching = defaultProject?.id;

  // Company-change blanking. The Provider's cascading reset wipes the
  // pickUp/dropOff/staging *form values* when the company changes, but the
  // SiteSelection display is driven by this panel's local state, so the value
  // alone clearing leaves the selectors showing the old, wrong-company sites.
  // We reset that local state here using React's "adjust state while
  // rendering" pattern (a prev-value ref + setState during render) rather than
  // an effect — the panel grep gate forbids effects/watchers, and this pattern
  // applies the reset before paint so the user never sees a stale frame. The
  // ref is seeded with the mount-time company id so this is inert on first
  // render and only fires on a real company transition (mirrors the Provider's
  // prevCompanyIdRef gate). The `key={companyId}` on <SiteSelection> below
  // additionally forces it to drop any cached internal selection.
  const prevCompanyIdRef = useRef(companyId);
  if (prevCompanyIdRef.current !== companyId) {
    prevCompanyIdRef.current = companyId;
    setPickUpWaypoint(null);
    setDropOffWaypoint(null);
    setStagingWaypoint(null);
    setShowPickUpContact(false);
    setShowDropOffContact(false);
    setPickUpContacts([]);
    setDropOffContacts([]);
    // Invalidate any in-flight contact fetch from the previous company so it
    // can't resolve and write a stale contact after the cascade clears.
    latestRequestedSiteIdRef.current = { pickUpWayPoint: null, dropOffWayPoint: null };
  }

  // Async contact fetch + autopopulate. Mirrors the legacy form's
  // setSiteContact, but triggered inline from the
  // SiteSelection callback so the panel stays free of side-effect hooks.
  //
  // Discarded-site guard: skip the contact fetch when the *incoming* site is
  // soft-deleted. Legacy keyed this off the mount-time default waypoint's
  // `discardedAt` (passing `defaultPickUpWaypoint?.site
  // ?.discardedAt`), which is stale: once a saved site loaded discarded, the
  // flag stayed true for every later selection, so replacing it with a fresh
  // active site never fetched the new site's contacts (Name/Phone stayed
  // blank). We read `site.discardedAt` instead — the site we're actually about
  // to fetch for. The mount-time re-emit of the loaded site is already filtered
  // out by the `site.id !== previousSiteId` guard in the onSiteChange handlers,
  // so this only runs on a genuine user pick.
  const populateSiteContact = useCallback(
    async (
      waypointType: 'pickUpWayPoint' | 'dropOffWayPoint',
      site: Nullable<SiteBasic | Site>,
    ) => {
      if (!companyId || !site?.id) return;
      if (site.discardedAt) return;

      const siteContacts = await getSiteContacts({ companyId, siteId: site.id });
      // Stale-response guard (race): a newer pick/clear for this waypoint
      // superseded this fetch while it was in flight — bail so an out-of-order
      // resolution can't stomp the active site's contact. See
      // latestRequestedSiteIdRef.
      if (latestRequestedSiteIdRef.current[waypointType] !== site.id) return;
      if (siteContacts.length === 0) return;

      const firstContact = siteContacts[0];
      if (waypointType === 'pickUpWayPoint') {
        setPickUpContacts(siteContacts);
        setShowPickUpContact(true);
        setLastSelectedPickUpPhone(firstContact.phone || '');
        setLastSelectedPickUpName(firstContact.name || '');
      } else {
        setDropOffContacts(siteContacts);
        setShowDropOffContact(true);
        setLastSelectedDropOffPhone(firstContact.phone || '');
        setLastSelectedDropOffName(firstContact.name || '');
      }
      setValue(
        `${waypointType}.contact`,
        {
          name: firstContact.name,
          phone: firstContact.phone,
        },
        { shouldDirty: false },
      );
    },
    [companyId, getSiteContacts, setValue],
  );

  const toggleOnPickUpContact = useCallback(() => {
    setShowPickUpContact(true);
  }, []);

  const toggleOffPickUpContact = useCallback(() => {
    // shouldDirty: true is what gets the cleared waypoint into the sparse
    // PATCH. Without it, deparseUpdateV2 sees an unchanged dirtyFields and
    // drops `pickUpWayPoint` entirely → ProjectDialog ends up with an
    // empty patch and skips the API call ("nothing gets submitted").
    setValue('pickUpWayPoint.contact', { name: '', phone: '' }, { shouldDirty: true });
    void trigger('pickUpWayPoint.contact');
    setShowPickUpContact(false);
  }, [setValue, trigger]);

  const toggleOnDropOffContact = useCallback(() => {
    setShowDropOffContact(true);
  }, []);

  const toggleOffDropOffContact = useCallback(() => {
    setValue('dropOffWayPoint.contact', { name: '', phone: '' }, { shouldDirty: true });
    void trigger('dropOffWayPoint.contact');
    setShowDropOffContact(false);
  }, [setValue, trigger]);

  // When the user clears a waypoint via the `X` in the site dropdown:
  // if the waypoint was already persisted (saved id present), we MUST keep
  // that id in form state so the deparser can emit `{ id, _destroy: 1 }`.
  // Setting the form value to null instead drops the entry from the PATCH
  // entirely → BE keeps the previously-saved waypoint → user sees the
  // site reappear on next open. Legacy injects the same destroy marker at
  // submit time; we do it eagerly here.
  const buildDestroyMarker = useCallback(
    (
      savedId: string | undefined,
      type: WaypointType,
      ordinality: number,
    ): WaypointFormValue => {
      if (!savedId) return null;
      return {
        id: savedId,
        type,
        ordinality,
        contact: { name: '', phone: '' },
        site: { id: '', name: '' },
      };
    },
    [],
  );

  const onPickUpSiteChange = useCallback(
    (site?: Site) => {
      // Snapshot the previous site id BEFORE we mutate state so we can
      // tell a real user pick (id changed) apart from SiteSelection's
      // mount-time re-emit of the loaded site (id unchanged). On edit-
      // mode mount, SiteSelection fires onPickUpSiteChange with the
      // already-saved site; populating in that case would clobber the
      // user's persisted contact with the site's default contact (the
      // exact regression this guard prevents).
      const previousSiteId = pickUpWaypoint?.site?.id;

      // Record the active selection so a slower in-flight contact fetch for a
      // now-superseded site can't stomp this one on resolution (see
      // latestRequestedSiteIdRef).
      latestRequestedSiteIdRef.current.pickUpWayPoint = site?.id ?? null;

      let newPickUpWaypoint: Nullable<WayPoint> = null;
      let newPickUpFormValue: WaypointFormValue = null;
      if (site) {
        const pickupWaypointId = pickUpWaypoint?.id ?? defaultPickUpWaypoint?.id;
        const pickupWaypointContact = pickUpWaypoint?.contact ?? undefined;
        newPickUpWaypoint = createWaypoint(
          site,
          WaypointType.PICKUP,
          10,
          pickupWaypointId,
          pickupWaypointContact ?? undefined,
        );
        newPickUpFormValue = createWaypointFormValue(
          site,
          WaypointType.PICKUP,
          10,
          pickupWaypointId,
          pickupWaypointContact ?? undefined,
        );
      } else {
        // Site cleared. Preserve the saved id (if any) as a destroy marker.
        const savedId = pickUpWaypoint?.id ?? defaultPickUpWaypoint?.id;
        newPickUpFormValue = buildDestroyMarker(savedId, WaypointType.PICKUP, 10);
      }

      setValue('pickUpWayPoint', newPickUpFormValue, { shouldDirty: true });
      setPickUpWaypoint(newPickUpWaypoint);
      if (!newPickUpWaypoint) {
        toggleOffPickUpContact();
        return;
      }
      // Only autopopulate when the site actually changed. Same-id call =
      // mount-time re-emit; skip so persisted contact stays intact.
      if (site && site.id !== previousSiteId) {
        void populateSiteContact('pickUpWayPoint', site);
      }
    },
    [
      pickUpWaypoint,
      defaultPickUpWaypoint?.id,
      setValue,
      toggleOffPickUpContact,
      populateSiteContact,
      buildDestroyMarker,
    ],
  );

  const onDropOffSiteChange = useCallback(
    (site?: Site) => {
      // See onPickUpSiteChange — same mount-time re-emit guard.
      const previousSiteId = dropOffWaypoint?.site?.id;

      // See onPickUpSiteChange — record the active selection for the race guard.
      latestRequestedSiteIdRef.current.dropOffWayPoint = site?.id ?? null;

      let newDropOffWaypoint: Nullable<WayPoint> = null;
      let newDropOffFormValue: WaypointFormValue = null;
      if (site) {
        const dropoffWaypointId = dropOffWaypoint?.id ?? defaultDropOffWaypoint?.id;
        const dropoffWaypointContact = dropOffWaypoint?.contact ?? undefined;
        newDropOffWaypoint = createWaypoint(
          site,
          WaypointType.DROP_OFF,
          20,
          dropoffWaypointId,
          dropoffWaypointContact ?? undefined,
        );
        newDropOffFormValue = createWaypointFormValue(
          site,
          WaypointType.DROP_OFF,
          20,
          dropoffWaypointId,
          dropoffWaypointContact ?? undefined,
        );
      } else {
        const savedId = dropOffWaypoint?.id ?? defaultDropOffWaypoint?.id;
        newDropOffFormValue = buildDestroyMarker(savedId, WaypointType.DROP_OFF, 20);
      }

      setValue('dropOffWayPoint', newDropOffFormValue, { shouldDirty: true });
      setDropOffWaypoint(newDropOffWaypoint);
      if (!newDropOffWaypoint) {
        toggleOffDropOffContact();
        return;
      }
      if (site && site.id !== previousSiteId) {
        void populateSiteContact('dropOffWayPoint', site);
      }
    },
    [
      dropOffWaypoint,
      defaultDropOffWaypoint?.id,
      setValue,
      toggleOffDropOffContact,
      populateSiteContact,
      buildDestroyMarker,
    ],
  );

  const onStagingSiteChange = useCallback(
    (site?: Site) => {
      let newStagingWaypoint: Nullable<WayPoint> = null;
      let newStagingFormValue: WaypointFormValue = null;
      if (site) {
        const stagingWaypointId = stagingWaypoint?.id ?? defaultStagingWaypoint?.id;
        newStagingWaypoint = createWaypoint(
          site,
          WaypointType.STAGING,
          15,
          stagingWaypointId,
        );
        newStagingFormValue = createWaypointFormValue(
          site,
          WaypointType.STAGING,
          15,
          stagingWaypointId,
        );
      } else {
        const savedId = stagingWaypoint?.id ?? defaultStagingWaypoint?.id;
        newStagingFormValue = buildDestroyMarker(savedId, WaypointType.STAGING, 15);
      }

      setValue('stagingWayPoint', newStagingFormValue, { shouldDirty: true });
      setStagingWaypoint(newStagingWaypoint);
    },
    [stagingWaypoint, defaultStagingWaypoint?.id, setValue, buildDestroyMarker],
  );

  return (
    <Card sx={{ p: 2, mb: 2 }} data-test-id="project-form-v2-location-details">
      <Typography variant="form-section-title" sx={{ mb: 2 }}>
        {t('order.form.location_details')}
      </Typography>
      <SiteSelection
        key={companyId ?? 'no-company'}
        allowRemoveDefaultSites
        errors={formState.errors}
        isRequired={false}
        companyId={companyId}
        projectId={projectIdForDataFetching}
        pickUpSite={pickUpWaypoint?.site}
        dropOffSite={dropOffWaypoint?.site}
        stagingSite={stagingWaypoint?.site}
        onPickUpSiteChange={onPickUpSiteChange}
        onDropOffSiteChange={onDropOffSiteChange}
        onStagingSiteChange={onStagingSiteChange}
      />
      {!showPickUpContact && (
        <PseudoLink
          title={`+ ${t('order.form.add_pick_up_site_contact')}`}
          action={toggleOnPickUpContact}
          dataTestId="project-form-v2-pickup-contact-toggle"
        />
      )}
      <Collapse in={showPickUpContact}>
        <Grid container spacing={2} data-test-id="project-form-v2-pickup-contact">
          <Grid size={{ xs: 6 }}>
            <AutocompleteFormField
              key={`pickup-contact-name-${showPickUpContact}-${lastSelectedPickUpName}`}
              control={control}
              name="pickUpWayPoint.contact.name"
              errors={formState.errors}
              sx={{ mr: 2 }}
              list={pickUpContacts}
              label={t('form_fields.name')}
              getValue={(item) => item?.name || item}
              getLabel={(item) => item?.name || item || ''}
              clearable={true}
              disabled={!companyId}
              isRequired={false}
              freeSolo
              onSelect={(item) => {
                setValue('pickUpWayPoint.contact.name', item?.name || item || '', {
                  shouldDirty: false,
                });
                setValue('pickUpWayPoint.contact.phone', item?.phone || '', {
                  shouldDirty: false,
                });
                setLastSelectedPickUpPhone(item?.phone || '');
                setLastSelectedPickUpName(item?.name || item || '');
              }}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <PhoneCodeFlagInput
              key={`pickup-contact-phone-${showPickUpContact}-${lastSelectedPickUpPhone}`}
              control={control}
              name="pickUpWayPoint.contact.phone"
              label={t('form_fields.phone')}
              isDisabled={!companyId}
              isRequired={false}
            />
          </Grid>
        </Grid>
        <PseudoLink
          title={t('order.form.remove_pick_up_site_contact')}
          sx={{ mt: 1 }}
          action={toggleOffPickUpContact}
          dataTestId="project-form-v2-pickup-contact-remove"
        />
      </Collapse>
      {!showDropOffContact && (
        <PseudoLink
          title={`+ ${t('order.form.add_drop_off_site_contact')}`}
          action={toggleOnDropOffContact}
          dataTestId="project-form-v2-dropoff-contact-toggle"
        />
      )}
      <Collapse in={showDropOffContact}>
        <Grid container spacing={2} data-test-id="project-form-v2-dropoff-contact">
          <Grid size={{ xs: 6 }}>
            <AutocompleteFormField
              key={`dropoff-contact-name-${showDropOffContact}-${lastSelectedDropOffName}`}
              control={control}
              name="dropOffWayPoint.contact.name"
              errors={formState.errors}
              sx={{ mr: 2 }}
              list={dropOffContacts}
              label={t('form_fields.name')}
              getValue={(item) => item?.name || item}
              getLabel={(item) => item?.name || item || ''}
              clearable={true}
              disabled={!companyId}
              isRequired={false}
              freeSolo
              onSelect={(item) => {
                setValue('dropOffWayPoint.contact.name', item?.name || item || '', {
                  shouldDirty: false,
                });
                setValue('dropOffWayPoint.contact.phone', item?.phone || '', {
                  shouldDirty: false,
                });
                setLastSelectedDropOffPhone(item?.phone || '');
                setLastSelectedDropOffName(item?.name || item || '');
              }}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <PhoneCodeFlagInput
              key={`dropoff-contact-phone-${showDropOffContact}-${lastSelectedDropOffPhone}`}
              control={control}
              name="dropOffWayPoint.contact.phone"
              label={t('form_fields.phone')}
              isDisabled={!companyId}
              isRequired={false}
            />
          </Grid>
        </Grid>
        <Box sx={{ mt: 1 }}>
          <PseudoLink
            title={t('order.form.remove_drop_off_site_contact')}
            action={toggleOffDropOffContact}
            dataTestId="project-form-v2-dropoff-contact-remove"
          />
        </Box>
      </Collapse>
    </Card>
  );
}
